import React, { useState, useEffect, useRef } from 'react';
import { useNav } from '../App';
import { Box, Typography, Avatar, CircularProgress, Snackbar, Alert, IconButton, Popover } from '@mui/material';
import api from '../services/api';
import { formatAgeBand } from '../utils/formatAgeBand';
import { C, SP, SZ } from '../theme';
import { IconWave } from '../BinkoIcons';


// ── WAVE BUTTON ANIMATION ─────────────────────────────────────────
const waveAnimStyle = document.createElement('style');
waveAnimStyle.id = 'binko-wave-anim';
waveAnimStyle.textContent = `
  @keyframes binko-shimmer {
    0%   { background-position: -200% center; }
    100% { background-position: 200% center; }
  }
  @keyframes binko-wave-icon {
    0%, 100% { transform: rotate(0deg); }
    20%       { transform: rotate(-18deg); }
    40%       { transform: rotate(14deg); }
    60%       { transform: rotate(-10deg); }
    80%       { transform: rotate(6deg); }
  }
  .binko-wave-btn {
    position: relative;
    overflow: hidden;
    background: linear-gradient(
      105deg,
      #FBBC15 0%,
      #FBBC15 40%,
      #ffe680 50%,
      #FBBC15 60%,
      #FBBC15 100%
    ) !important;
    background-size: 200% auto !important;
    animation: binko-shimmer 2.6s linear infinite !important;
  }
  .binko-wave-btn:active {
    transform: scale(0.97);
  }
  .binko-wave-icon {
    animation: binko-wave-icon 2.6s ease-in-out infinite;
    display: inline-block;
    transform-origin: bottom center;
  }
`;
if (!document.getElementById('binko-wave-anim')) {
  document.head.appendChild(waveAnimStyle);
}

// ── STYLES ────────────────────────────────────────────────────────
const S = {
  root: {
    minHeight: '100vh',
    background: C.bgApp,
    padding: `16px ${SP.screenH}px`,
    display: 'flex',
    flexDirection: 'column',
    gap: `${SP.md}px`,
    paddingBottom: `${SZ.navHeight + SP.lg}px`,
  },
  title: {
    fontSize: '22px',
    fontWeight: 600,
    color: C.textPrimary,
    letterSpacing: '-0.3px',
  },
  sessionBar: {
    background: C.bgCard,
    border: `0.5px solid ${C.border}`,
    borderRadius: '12px',
    padding: `${SP.sm}px ${SP.md}px`,
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  sessionVenue: {
    fontSize: '13px',
    color: C.textPrimary,
    fontWeight: 500,
  },
  sessionWaves: {
    fontSize: '12px',
    color: C.teal,
    fontWeight: 500,
  },
  emptyBox: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    flex: 1,
    gap: `${SP.sm}px`,
    paddingTop: '60px',
  },
  emptyText: {
    fontSize: '13px',
    color: C.textMuted,
    textAlign: 'center',
    lineHeight: 1.5,
  },
  card: {
    background: C.bgCard,
    border: `0.5px solid ${C.border}`,
    borderRadius: '14px',
    padding: `${SP.cardPad}px`,
    display: 'flex',
    flexDirection: 'column',
    gap: `${SP.md}px`,
    transition: 'border-color 0.15s',
  },
  cardTop: {
    display: 'flex',
    alignItems: 'center',
    gap: `${SP.md}px`,
  },
  nameRow: {
    fontSize: '15px',
    fontWeight: 500,
    color: C.textPrimary,
    lineHeight: 1.2,
  },
  gameScore: {
    fontSize: '11px',
    color: C.orange,
    fontWeight: 500,
    marginTop: '2px',
    letterSpacing: '0.04em',
  },
  idRow: {
    fontSize: '13px',
    color: C.textMuted,
    lineHeight: 1.4,
  },
  waveBtn: {
    background: C.yellow,
    border: 'none',
    borderRadius: '12px',
    height: `${SZ.btnPrimary}px`,
    fontSize: '13px',
    fontWeight: 600,
    color: C.yellowText,
    cursor: 'pointer',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
    transition: 'background 0.15s',
  },
  waveBtnDone: {
    background: C.bgElevated,
    border: 'none',
    borderRadius: '12px',
    height: `${SZ.btnPrimary}px`,
    fontSize: '13px',
    fontWeight: 600,
    color: C.textDisabled,
    cursor: 'default',
    width: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '6px',
    fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif',
  },
};

function Browse() {
  const navigate = useNav();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mySession, setMySession] = useState(null);
  const [toast, setToast] = useState({ open: false, message: '', severity: 'success' });
  const [waveAnchorEl, setWaveAnchorEl] = useState(null);
  const [likedIds, setLikedIds] = useState(new Set());

  const intervalRef = useRef(null);
  const isMounted = useRef(true);

  useEffect(() => {
    isMounted.current = true;
    fetchUsers();
    intervalRef.current = setInterval(fetchUsers, 5000);
    return () => {
      isMounted.current = false;
      clearInterval(intervalRef.current);
    };
  }, []);

  const fetchUsers = async () => {
    try {
      const res = await api.get('/browse');
      if (!isMounted.current) return;
      setUsers(res.data.users || []);
      setMySession(res.data.mySession);
    } catch (err) {
      console.error('Browse fetch failed:', err);
      if (!isMounted.current) return;
      clearInterval(intervalRef.current);
    }
    if (isMounted.current) setLoading(false);
  };

  const handleWave = async (userId) => {
    if (likedIds.has(userId)) return;
    setLikedIds(prev => new Set([...prev, userId]));
    try {
      const res = await api.post('/like', { likedUserId: userId });
      if (res.data.matched) {
        const matchId    = res.data.matchId    || '';
        const theirAlias = res.data.theirAlias || '';
        if (matchId && !sessionStorage.getItem('match_overlay_seen_' + matchId)) {
          window.dispatchEvent(new CustomEvent('binko:match', {
            detail: { matchId, theirAlias },
          }));
          sessionStorage.setItem('match_overlay_seen_' + matchId, '1');
        }
      } else {
        setToast({ open: true, message: '👋 Wave sent!', severity: 'info' });
      }
      fetchUsers();
    } catch (err) {
      setLikedIds(prev => { const s = new Set(prev); s.delete(userId); return s; });
      setToast({ open: true, message: err.response?.data?.message || 'Could not send wave', severity: 'error' });
    }
  };

  const formatScore = (n) => {
    if (!n || n === 0) return null;
    if (n >= 1000000) return (n / 1000000).toFixed(1).replace(/\.0$/, '') + 'M';
    if (n >= 1000) return (n / 1000).toFixed(1).replace(/\.0$/, '') + 'K';
    return String(n);
  };

  return (
    <Box sx={S.root}>

      <Typography sx={S.title}>In Here</Typography>

      {mySession && (
        <Box sx={S.sessionBar}>
          <Typography sx={S.sessionVenue}>📍 {mySession.venue_name}</Typography>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
            <Typography sx={S.sessionWaves}>
              👋 {mySession.likes_remaining} left today
            </Typography>
            <IconButton
              size="small"
              onClick={(e) => setWaveAnchorEl(e.currentTarget)}
              sx={{ color: C.teal, padding: '2px' }}
            >
              <span style={{ fontSize: '14px', lineHeight: 1 }}>ⓘ</span>
            </IconButton>
          </Box>
          <Popover
            open={Boolean(waveAnchorEl)}
            anchorEl={waveAnchorEl}
            onClose={() => setWaveAnchorEl(null)}
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            PaperProps={{
              sx: {
                background: C.bgCard,
                border: `0.5px solid ${C.border}`,
                borderRadius: '12px',
                padding: '12px',
                maxWidth: '220px',
              }
            }}
          >
            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: C.textPrimary, mb: '6px' }}>
              What’s a wave?
            </Typography>
            <Typography sx={{ fontSize: '13px', color: C.textMuted, lineHeight: 1.5 }}>
              Tap Wave on someone’s card to show interest. If they wave back, it’s a match and you can chat. You get 3 waves per check-in.
            </Typography>
          </Popover>
        </Box>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', pt: 8 }}>
          <CircularProgress />
        </Box>
      ) : users.length === 0 ? (
        <Box sx={S.emptyBox}>
          <Typography sx={{ fontSize: '32px' }}>👀</Typography>
          <Typography sx={S.emptyText}>
            No one else here yet.{'\n'}Be the first to check in!
          </Typography>
        </Box>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: `${SP.sm}px` }}>
          {users.map((user) => {
            const waved = likedIds.has(user.id) || user.hi_status === 'already_liked' || user.hi_status === 'already_matched';
            const score = formatScore(user.game_score_total);
            const wins = user.tournament_wins || 0;
            const ageBand = formatAgeBand(user.date_of_birth);

            return (
              <Box key={user.id} sx={S.card}>

                <Box sx={S.cardTop}>
                  <Avatar sx={{ width: SZ.avatarLg, height: SZ.avatarLg, fontSize: '17px' }}>
                    {user.alias.charAt(0).toUpperCase()}
                  </Avatar>
                  <Box>
                    {/* Name + age on one line — design system rule */}
                    <Typography sx={S.nameRow}>
                      {user.alias}{ageBand ? `, ${ageBand}` : ''}
                    </Typography>
                    {(score || wins > 0) && (
                      <Typography sx={S.gameScore}>
                        {score ? <>🎮 {score}</> : null}
                        {score && wins > 0 ? '  ' : null}
                        {wins > 0 ? <>🏆 {wins}</> : null}
                      </Typography>
                    )}
                  </Box>
                </Box>

                <Box sx={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                  {user.location_area && (
                    <Typography sx={S.idRow}>📍 {user.location_area}</Typography>
                  )}
                  {user.clothing_primary && (
                    <Typography sx={S.idRow}>
                      👕 {user.clothing_primary}
                      {user.clothing_secondary ? `, ${user.clothing_secondary}` : ''}
                    </Typography>
                  )}
                  {user.hair_type && (
                    <Typography sx={S.idRow}>
                      ✨ {user.hair_type}
                      {user.facial_hair && user.facial_hair !== 'None' ? `, ${user.facial_hair}` : ''}
                      {user.wearing_glasses_now ? ', Glasses' : ''}
                    </Typography>
                  )}
                  {user.accessories && user.accessories !== 'Nothing noticeable' && (
                    <Typography sx={S.idRow}>🎒 {user.accessories}</Typography>
                  )}
                  {user.group_status && (
                    <Typography sx={S.idRow}>👥 {user.group_status}</Typography>
                  )}
                </Box>

                <button
                  style={waved ? S.waveBtnDone : S.waveBtn}
                  className={waved ? '' : 'binko-wave-btn'}
                  onClick={() => handleWave(user.id)}
                  disabled={waved}
                >
                  <span className={waved ? '' : 'binko-wave-icon'}>
                    <IconWave size={18} color={waved ? C.textDisabled : C.yellowText} />
                  </span>
                  {waved ? 'Waved' : 'Wave'}
                </button>

              </Box>
            );
          })}
        </Box>
      )}

      <Snackbar
        open={toast.open}
        autoHideDuration={3500}
        onClose={() => setToast(t => ({ ...t, open: false }))}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          severity={toast.severity}
          onClose={() => setToast(t => ({ ...t, open: false }))}
          sx={{ width: '100%', fontFamily: '"Plus Jakarta Sans", system-ui, sans-serif' }}
        >
          {toast.message}
        </Alert>
      </Snackbar>

    </Box>
  );
}

export default Browse;
